// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@steiner/App/Components/Common/XngZone";
import HomeAccordion, { HomeAccordionStyles } from "@steiner/App/Components/Home/HomeAccordion";
import { CustomAppState, VideoDocumentModel } from "@steiner/App/Store/Reducers";
import { getProductVideos } from "@steiner/App/Store/Selectors/ProductDetailsSelector";
import DataTable from "@steiner/MobiusOverrides/DataTable";
import TabGroup from "@steiner/MobiusOverrides/TabGroup";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// XNG-Change add mapStateToProps
const mapStateToProps = (state: CustomAppState) => {
    const substituteProductsCarouselId = state.xProductDetails.substituteProductsCarouselId;
    return {
        substituteProductsCarouselId,
        substituteProductsLength:
            getProductsForProductInfoList(state, substituteProductsCarouselId as string)?.length || 0,
    };
};

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
    // XNG-Changes
    showVideos = "showVideos",
    showSubstituteProducts = "showSubstituteProducts",
    showApplications = "showApplications",
}

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    // XNG-Changes
    attributeTypeLabelText?: DataTableCellProps;
    attributeTypeValuesText?: DataTableCellProps;
    container?: InjectableCss;
    tabsGroup?: TabGroupPresentationProps;
    tabContainer?: TabPresentationProps;
    attributeTypeContent?: InjectableCss;
    attributeType?: InjectableCss;
    specificationsWrapper?: InjectableCss;
    videosWrapper?: InjectableCss;
    videoLink?: LinkPresentationProps;
    documentsTypeContent?: InjectableCss;
    homeAccordionStyles?: HomeAccordionStyles;
    dataTableRow?: DataTableRowProps;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    // attributeTypeLabelText: {
    //     weight: "bold",
    // },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    id,
    product,
    product: { brand, attributeTypes, documents, specifications },
    fields: {
        showDocuments,
        showAttributes,
        documentsPosition,
        attributesPosition,
        // XNG-Changes
        showVideos,
        showSubstituteProducts,
    },
    // XNG-Change
    substituteProductsCarouselId,
    substituteProductsLength,
}) => {
    const renderingDocuments = showDocuments && documents && documents.length > 0;
    const renderingAttributes = showAttributes && (brand || (attributeTypes && attributeTypes?.length > 0));
    const renderingSpecifications = specifications && specifications.length > 0;
    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";

    const { isInShell } = useShellContext();

    // XNG-Changes
    const { videos } = useAppSelector(state => {
        return {
            videos: getProductVideos(state as CustomAppState, product, true),
        };
    });
    const renderingVideos = showVideos && videos && videos.length > 0;

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications && !renderingVideos) {
        return null;
    }

    const renderVideo = (video: VideoDocumentModel, index: number) => {
        return (
            <Link href={video.videoLink} target="_blank" {...styles.videoLink} key={video.videoLink}>
                {video.videoLink}
            </Link>
        );
    };

    const renderDocuments = () => {
        return (
            <StyledWrapper {...styles.documentsTypeContent}>
                <DataTable useMobileLayout={false} usePrintLayout={false}>
                    <DataTableBody>
                        {documents!.map(document => (
                            <DataTableRow key={document.id.toString()} {...styles.dataTableRow}>
                                <DataTableCell>
                                    <Link
                                        href={document.filePath}
                                        target="_new"
                                        {...styles.documentLink}
                                        data-test-selector={`productDetails_${document.id}`}
                                        aria-label={`${document.name} Link`}
                                        key={document.id.toString()}
                                    >
                                        <Typography {...styles.documentNameText}>{document.description}</Typography>
                                    </Link>
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        );
    };

    const renderAttributes = () => {
        return (
            attributeTypes && (
                <DataTable useMobileLayout={false} usePrintLayout={false}>
                    <DataTableBody>
                        {attributeTypes.map(attributeType => (
                            <DataTableRow key={attributeType.id.toString()} {...styles.dataTableRow}>
                                <DataTableCell {...styles.attributeTypeLabelText}>
                                    {attributeType.label || attributeType.name}
                                </DataTableCell>
                                <DataTableCell {...styles.attributeTypeValuesText}>
                                    {attributeType
                                        .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                        .join(", ")}
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            )
        );
    };

    const documentsAccordion = renderingDocuments && (
        <HomeAccordion
            title={translate("Documents")}
            isLoading={false}
            showViewAll={false}
            showPagination={false}
            data-test-selector="productDetails_specifications_documents"
            initialExpanded={
                ((!attributesFirst || !renderingAttributes) && documentsFirst) ||
                (!renderingSpecifications && !renderingAttributes)
            }
            extendedStyles={styles.homeAccordionStyles}
        >
            {renderDocuments()}
        </HomeAccordion>
    );

    const attributesAccordion = renderingAttributes && (
        <HomeAccordion
            title={translate("Attributes")}
            isLoading={false}
            showViewAll={false}
            showPagination={false}
            initialExpanded={attributesFirst || (!renderingSpecifications && !renderingDocuments)}
            data-test-selector="productDetails_specifications_attributes"
            parsedClassName="attributesAccordion"
            extendedStyles={styles.homeAccordionStyles}
        >
            {renderAttributes()}
        </HomeAccordion>
    );

    const videosAccordion = (
        <HomeAccordion
            title={translate("Media")}
            isLoading={false}
            showViewAll={false}
            showPagination={false}
            data-test-selector="productDetails_specifications_media"
            initialExpanded={false}
            extendedStyles={styles.homeAccordionStyles}
        >
            <StyledWrapper {...styles.videosWrapper}>
                {videos?.map((productVideo: VideoDocumentModel, index: number) => renderVideo(productVideo, index))}
            </StyledWrapper>
        </HomeAccordion>
    );

    const substituteAccordion = (
        <HomeAccordion
            title={translate("Substitute Products")}
            isLoading={false}
            showViewAll={false}
            showPagination={false}
            data-test-selector="productDetails_specifications_substituteProducts"
            initialExpanded={false}
            parsedClassName="substituteProductsAccordion attributesAccordion"
        >
            <XngZone contentId={id} zoneName="Content01" hint="Zone For Substitute Products" />
        </HomeAccordion>
    );

    const documentsTab = renderingDocuments && (
        <Tab
            headline={translate("Documents & Resources")}
            data-test-selector="productDetails_specifications_documents"
            tabKey="documents-tab"
            key="documents"
            {...styles.tabContainer}
            role="tab"
        >
            {renderDocuments()}
        </Tab>
    );

    const attributesTab = renderingAttributes && (
        <Tab
            headline={translate("Attributes")}
            data-test-selector="productDetails_specifications_attributes"
            tabKey="attributes-tab"
            key="attributes"
            {...styles.tabContainer}
            role="tab"
        >
            <StyledWrapper {...styles.attributeTypeContent}>{renderAttributes()}</StyledWrapper>
        </Tab>
    );

    const specificationTabs =
        renderingSpecifications &&
        specifications.map(specification => (
            <Tab
                headline={translate(specification.nameDisplay || specification.name || "Specifications")}
                data-test-selector="productDetails_specifications_specification"
                key={specification.id.toString()}
                tabKey={`${specification.id.toString() + specification.nameDisplay.toLowerCase()}-tab`}
                {...styles.tabContainer}
                role="tab"
            >
                <StyledWrapper {...styles.specificationsWrapper}>
                    {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                </StyledWrapper>
            </Tab>
        ));

    const videosTabs = renderingVideos && (
        <Tab
            headline={translate("Media")}
            data-test-selector="productDetails_specifications_media"
            key={translate("Media")}
            tabKey="media-tab"
            {...styles.tabContainer}
            role="tab"
        >
            <StyledWrapper {...styles.videosWrapper}>
                {videos?.map((productVideo: VideoDocumentModel, index: number) => renderVideo(productVideo, index))}
            </StyledWrapper>
        </Tab>
    );

    const substituteProductsTabs = (showSubstituteProducts || isInShell) &&
        (!substituteProductsCarouselId || (substituteProductsCarouselId && substituteProductsLength > 0)) && (
            <Tab
                headline={translate("Substitute Products")}
                data-test-selector="productDetails_specifications_substituteProducts"
                key={translate("Substitute Products")}
                tabKey="substitute-products-tab"
                {...styles.tabContainer}
                role="tab"
            >
                <XngZone contentId={id} zoneName="Content01" hint="Zone For Substitute Products" />
            </Tab>
        );

    const tabs = [];

    if (attributesTab && attributesFirst) {
        tabs.push(attributesTab);
    }
    if (documentsTab && documentsFirst) {
        tabs.push(documentsTab);
    }
    if (specificationTabs) {
        tabs.push(...specificationTabs);
    }
    if (attributesTab && !attributesFirst) {
        tabs.push(attributesTab);
    }
    if (documentsTab && !documentsFirst) {
        tabs.push(documentsTab);
    }
    if (videosTabs) {
        tabs.push(videosTabs);
    }
    if (substituteProductsTabs) {
        tabs.push(substituteProductsTabs);
    }

    // XNG-Change: Change structure
    return (
        <StyledWrapper {...styles.container}>
            <Hidden above="md">
                <Accordion
                    headingLevel={2}
                    data-test-selector="productDetails_specifications"
                    {...styles.specificationsAccordion}
                >
                    {attributesFirst && attributesAccordion}
                    {documentsFirst && documentsAccordion}
                    {renderingSpecifications &&
                        specifications?.map((specification, index) => {
                            return (
                                <HomeAccordion
                                    initialExpanded={
                                        index === 0 &&
                                        (!documentsFirst || !renderingDocuments) &&
                                        (!attributesFirst || !renderingAttributes)
                                    }
                                    isLoading={false}
                                    showViewAll={false}
                                    showPagination={false}
                                    key={specification.id.toString()}
                                    title={specification.nameDisplay}
                                    data-test-selector="productDetails_specifications_specification"
                                    extendedStyles={styles.homeAccordionStyles}
                                    // {...styles.specificationsAccordionSection}
                                >
                                    {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                                </HomeAccordion>
                            );
                        })}
                    {!attributesFirst && attributesAccordion}
                    {!documentsFirst && documentsAccordion}
                    {renderingVideos && videosAccordion}
                    {(showSubstituteProducts || isInShell) &&
                        (!substituteProductsCarouselId ||
                            (substituteProductsCarouselId && substituteProductsLength > 0)) &&
                        substituteAccordion}
                </Accordion>
            </Hidden>
            <Hidden below="lg">
                <TabGroup
                    data-test-selector="productDetails_specifications"
                    selectLabel={translate("Product Information")}
                    {...styles.tabsGroup}
                >
                    {tabs}
                </TabGroup>
            </Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsSpecifications)),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
            // XNG-Changes
            {
                name: fields.showVideos,
                displayName: "Show Videos",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showSubstituteProducts,
                displayName: "Show Substitute Products",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 6,
            },
        ],
    },
};

export default widgetModule;
